<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <!-- Local dev only: pick which seeded member to log in as (ONE-7804).
           This has to live on /login specifically. The router guard sends every
           tokenless path here in local mode, so /login is the ONLY route local
           mode can reach before a token exists — and without a token the local
           branch of ionViewWillEnter just returns, which would otherwise leave
           this page permanently blank. -->
      <LocalLogin v-if="isLocal && !hasLocalToken" />
      <div v-else class="login-container">
        <Loading v-if="isLoading" />
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage, isPlatform } from '@ionic/vue';
import { Browser } from '@capacitor/browser';
import { openAuthUrl } from '@/utils/safe-browser-open';
import { useAuth0, getAccessTokenSilentlyWithMonitoring } from '@/utils/auth0';
import { mapActions } from 'vuex';
import { App } from '@capacitor/app';
import Loading from '../../components/common/LoadingState.vue';
import LocalLogin from '@/components/Local/localLogin.vue';
import { buildPendingDeepLinkAppState } from '@/utils/deeplink';

export default {
  name: 'LoginPage',
  components: {
    IonContent,
    IonPage,
    Loading,
    LocalLogin,
  },
  data() {
    return {
      isLocal: process.env.VUE_APP_NODE_ENV === 'local',
      // Read once at create: if a token is already present, ionViewWillEnter
      // redirects to /pre-loading, and we don't want the picker to flash first.
      hasLocalToken: !!localStorage.getItem('token'),
      isLoading: false,
      isBrowserOpen: false,
      token: '',
      authCheckDebounce: null, // Single debounce timeout
      wasBackgrounded: false,
      browserWasOpenBeforeBackground: false,
      subs: [],
    };
  },
  setup() {
    const auth0 = useAuth0();
    return { auth0 };
  },

  async ionViewWillEnter() {
    const currentPath = this.$route?.path || '';

    // CRITICAL: Only run auth check if we're actually on the login route
    // Ionic Vue can trigger this hook even when navigating to other routes due to component caching
    if (!currentPath.includes('/login')) {
      return;
    }

    // In local mode, skip Auth0 entirely — if a token exists, go straight to pre-loading
    if (process.env.VUE_APP_NODE_ENV === 'local') {
      const token = localStorage.getItem('token');
      // Re-read on every entry, not just in data(). Ionic caches page components
      // (that's why this hook exists at all), so after logout — which clears the
      // token and routes back here — a value captured once at create would still
      // say "token present" and the picker would never re-render.
      this.hasLocalToken = !!token;
      if (token) {
        this.$store.commit('user/setToken', token);
        this.$router.replace('/pre-loading');
      }
      return;
    }

    if (this.browserWasOpenBeforeBackground && !this.isBrowserOpen) {
      this.browserWasOpenBeforeBackground = false;
      this.scheduleAuthCheck();
    } else {
      this.scheduleAuthCheck();
    }
  },
  ionViewWillLeave() {
    this.cleanup();
  },

  async created() {
    this.subs.push(
      // Deep links
      await App.addListener('appUrlOpen', async data => {
        // Only handle if we're on the login route
        if (!this.$route.path.includes('login')) {
          return;
        }
        this.isLoading = true;
        // Close browser, but don't let errors block auth check
        try {
          await this.closeBrowser();
        } catch (error) {
          // Browser may already be closed by Auth0 redirect - that's okay
          console.log('[Login] Browser close error in appUrlOpen (non-blocking):', error?.message);
        }
        this.scheduleAuthCheck();
      }),
    );

    // iOS browser close
    if (isPlatform('ios')) {
      this.subs.push(
        await Browser.addListener('browserFinished', () => {
          // Only handle if we're on the login route
          if (!this.$route.path.includes('login')) {
            return;
          }
          if (this.isBrowserOpen) {
            this.isBrowserOpen = false;
            this.browserWasOpenBeforeBackground = false;
            this.scheduleAuthCheck();
          }
        }),
      );
    }

    this.subs.push(
      await App.addListener('resume', async () => {
        // Specifically handles app icon click to relaunch
        // iOS: if a SafariViewController was left open, close it now so it doesn't overlay the app UI
        if (isPlatform('ios') && this.isBrowserOpen) {
          try {
            await this.closeBrowser(); // <-- proactively dismiss the sheet
          } catch (error) {
            // Browser may already be closed - that's okay
            console.log('[Login] Browser close error in resume (non-blocking):', error?.message);
            this.isBrowserOpen = false;
            this.browserWasOpenBeforeBackground = false;
          }
        } else {
          // keep Android behaviour (or iOS when not open): reset flags
          this.isBrowserOpen = false;
          this.browserWasOpenBeforeBackground = false;
        }
        if (this.$route.path.includes('login') && !this.auth0.isAuthenticated.value) {
          this.isLoading = true;
          this.scheduleAuthCheck();
        }
      }),
    );

    this.subs.push(
      await App.addListener('pause', () => {
        // Ensures we catch backgrounding
        this.wasBackgrounded = true;
        if (this.isBrowserOpen) {
          this.browserWasOpenBeforeBackground = true;
        }
      }),
    );
  },

  beforeUnmount() {
    console.log('beforeUnmount');
    this.cleanup();
    this.subs.forEach(h => h?.remove && h.remove());
    this.subs = [];
  },

  methods: {
    ...mapActions('user', ['setToken']),

    // Single debounced auth check - prevents multiple simultaneous calls
    scheduleAuthCheck() {
      console.log('[Login] scheduleAuthCheck() called');
      // Clear any existing scheduled check
      if (this.authCheckDebounce) {
        clearTimeout(this.authCheckDebounce);
      }

      this.isLoading = true;

      // Schedule new check
      this.authCheckDebounce = setTimeout(async () => {
        console.log('[Login] Executing auth check');
        await this.checkAuthAndAct();
        this.authCheckDebounce = null;
      }, 500);
    },

    // Single decision point for all auth logic
    async checkAuthAndAct() {
      try {
        await this.auth0.checkSession();
        if (this.auth0.isAuthenticated.value) {
          // Close browser first, but don't let errors block navigation
          try {
            await this.closeBrowser();
          } catch (error) {
            // Browser closing errors shouldn't prevent login completion
            console.log('[Login] Browser close error (non-blocking):', error?.message);
          }
          await this.getToken();
          return;
        }

        const shouldLogin = this.$route.path.includes('login') && !this.isBrowserOpen;

        if (shouldLogin) {
          await this.login();
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async getToken() {
      try {
        // Fix A — default cacheMode 'on' reads cache then silently refreshes. Only 'on' | 'off' | 'cache-only' are valid per @auth0/auth0-spa-js.
        this.token = await getAccessTokenSilentlyWithMonitoring({ cacheMode: 'on' }, 'login');
        this.setToken(this.token);

        if (this.$route.path.includes('login')) {
          this.$router.replace('/pre-loading');
        }
      } catch (e) {
        console.error('Error getting token:', e);
      }
    },

    async login() {
      // Critical guard - prevent multiple browsers
      if (this.isBrowserOpen) {
        console.log('Browser already open, aborting login');
        return;
      }

      console.log('Opening login browser');
      this.isBrowserOpen = true; // Set BEFORE opening browser
      this.browserWasOpenBeforeBackground = false;
      this.isLoading = true;

      const appState = buildPendingDeepLinkAppState();

      try {
        await this.auth0.loginWithRedirect({
          ...(appState ? { appState } : {}),
          openUrl: url => openAuthUrl(url, { source: 'login' }),
        });

        this.wasBackgrounded = false;
      } catch (error) {
        console.error('Login error:', error);
        this.isBrowserOpen = false;
      }
    },

    async closeBrowser() {
      if (this.isBrowserOpen) {
        console.log('Closing browser');
        try {
          await Browser.close();
        } catch (error) {
          // Ignore "No active window" errors - browser may already be closed
          // This can happen when Auth0 redirect callback closes the browser automatically
          // or when multiple components try to close the browser simultaneously
          if (
            error?.message?.includes('No active window') ||
            error?.message?.includes('No active window to close')
          ) {
            console.log('[Login] Browser already closed, ignoring error');
          } else {
            console.error('Error closing browser:', error);
          }
        }
      }
      this.isBrowserOpen = false;
      this.browserWasOpenBeforeBackground = false;
    },

    cleanup() {
      if (this.authCheckDebounce) {
        clearTimeout(this.authCheckDebounce);
        this.authCheckDebounce = null;
      }
      this.closeBrowser();
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
ion-content {
  --ion-background-color: #f2efed;
}

.login-container {
  background-color: #f2efed;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
